import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { PeriodTabs } from '../components/PeriodTabs';
import { RaporTablosu } from '../components/rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../components/rapor-ekrani/RaporHatasi';
import { FILTRE_GIRIS, FiltreAlani, RaporFiltreBari, gecerliTarih } from '../components/rapor-ekrani/RaporFiltreBari';
import { useDoluYukseklik } from '../components/rapor-ekrani/useDoluYukseklik';
import { useCekmeceSecimi } from '../components/rapor-ekrani/useCekmeceSecimi';
import { ekranKaynagi, useRaporSorgu } from '../components/rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../components/rapor-ekrani/useDarEkran';
import { borcAlacak, tarihMetni } from '../components/rapor-ekrani/bicim';
import type { RaporKolonu } from '../components/rapor-ekrani/kolon';
import { BaIsareti } from '../components/cari-hesap/BaIsareti';
import { CariHareketCekmecesi } from '../components/cari-hesap/CariHareketCekmecesi';
import {
  BAKIYE_SECENEKLERI, carileriSuz, cariSatiri,
  type BakiyeTipi, type CariSatiri, type HamCariSatiri,
} from '../components/cari-hesap/tipler';
import type { EkranProps } from '../ekranKayitDefteri';

const SAYI = new Intl.NumberFormat('tr-TR');

/**
 * S334: Cari Hesap Listesi (ERP12 rapor ekranlarının ilki; onaylı demo `tasks/s334/erp12-rapor-demosu.html`).
 * Liste `liste` ds'inden (`@SONTARIH`), satıra basınca açılan çekmece `hareket` ds'inden beslenir;
 * kaynaklar `firm_panel_screens.config.kaynaklar` sözlüğünden çözülür.
 *
 * Bakiye seçicisi ve arama İSTEMCİDE süzer (sorgu yinelenmez); "şu tarihe kadar" değişince liste
 * yeniden sorgulanır ve önceki liste o arada ekranda kalır. Toplam satırı süzülen satırların
 * tamamını toplar ve etiketi bunu söyler. Çekmecedeki cari URL'de durur (`?cari=<id>`).
 *
 * TELEFON (<= 767 px): liste KART (üstte cari adı, sağında bakiye + B/A; altında kod, son işlem, borç,
 * alacak), toplam altta yapışık şerit, sayfa tek kaydırma. Yapışık barda yalnız bakiye seçicisi durur;
 * tarih katlanır "Filtreler"in içinde, arama kart listesinin başında "Sırala"nın yanında.
 */
export default function CariHesapListesi({ screen }: EkranProps) {
  const listeKaynagi = ekranKaynagi(screen, 'liste');
  const hareketKaynagi = ekranKaynagi(screen, 'hareket');
  const belgeKaynagi = ekranKaynagi(screen, 'belge');
  const telefon = useDarEkran();
  // Masaüstünde kök ekran yüksekliğine kilitlenir (tablo kendi içinde kayar); telefonda yalnız en az o kadardır.
  const doluRef = useDoluYukseklik<HTMLDivElement>(telefon ? 'enAz' : 'yukseklik');

  const [tip, setTip] = useState<BakiyeTipi>('tumu');
  const [ara, setAra] = useState('');
  // Tarih iki durumdur: `sonGiris` alanda yazan, `son` sorguya giden. Elle yazarken ara değerler
  // ("0002-10-01") sorgu anahtarına girmez; ekran boşalmaz, gereksiz istek gitmez.
  const [bugun] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [son, setSon] = useState(bugun);
  const [sonGiris, setSonGiris] = useState(son);

  const q = useRaporSorgu<HamCariSatiri>(listeKaynagi, { '@SONTARIH': son });
  const tum = useMemo(() => (q.data ?? []).map(cariSatiri), [q.data]);
  // Arama tuş başına on binlerce satırı süzer: giriş alanı akıcı kalsın diye süzme ertelenir.
  const araGecikmeli = useDeferredValue(ara);
  const suzulen = useMemo(() => carileriSuz(tum, tip, araGecikmeli), [tum, tip, araGecikmeli]);

  // Temsilci ya da vade bütün satırlarda boşsa (firma o alanı kullanmıyor) kolon hiç gösterilmez.
  const temsilciVar = useMemo(() => tum.some((c) => c.temsilci !== ''), [tum]);
  const vadeVar = useMemo(() => tum.some((c) => c.vade !== null), [tum]);
  const kolonlar = useMemo<RaporKolonu<CariSatiri>[]>(() => {
    // `kart`: telefondaki kartta kolonun yeri. Temsilci ve vade kartta yok (çekmecenin alt başlığında).
    const k: (RaporKolonu<CariSatiri> | false)[] = [
      { id: 'cari_kodu', baslik: 'Cari Kodu', tur: 'kod', deger: (c) => c.kod, kart: 'alt' },
      { id: 'cari', baslik: 'Cari', tur: 'metin', deger: (c) => c.ad, esnek: true, kart: 'baslik' },
      temsilciVar && { id: 'temsilci', baslik: 'Temsilci', tur: 'metin', deger: (c) => c.temsilci, kart: 'gizli' },
      vadeVar && { id: 'vade', baslik: 'Vade (gün)', tur: 'sayi', deger: (c) => c.vade, kart: 'gizli' },
      { id: 'borc', baslik: 'Borç', tur: 'para', deger: (c) => c.borc, toplam: true, kart: 'ikincil' },
      { id: 'alacak', baslik: 'Alacak', tur: 'para', deger: (c) => c.alacak, toplam: true, kart: 'ikincil' },
      { id: 'bakiye', baslik: 'Bakiye', tur: 'para', deger: (c) => c.bakiye, toplam: true, kart: 'olcu' },
      {
        id: 'ba', baslik: 'B/A', tur: 'metin', hizala: 'orta', genislik: 56, kart: 'ek',
        deger: (c) => c.ba,
        // Toplamın işareti de toplam BAKİYEDEN (yuvarlanmış) seçilir.
        toplam: (s) => borcAlacak(s.reduce((t, c) => t + c.bakiye, 0)),
        hucre: (v) => <BaIsareti deger={v} />,
      },
      { id: 'son_islem_tarihi', baslik: 'Son İşlem', tur: 'tarih', deger: (c) => c.sonIslem, kart: 'alt', kartEtiketi: 'Son işlem' },
    ];
    return k.filter((x): x is RaporKolonu<CariSatiri> => x !== false);
  }, [temsilciVar, vadeVar]);

  const secim = useCekmeceSecimi('cari');
  const seciliCari = useMemo(
    () => (secim.secili ? tum.find((c) => c.id === secim.secili) : undefined),
    [tum, secim.secili],
  );
  const { ac } = secim;
  const satirTiklandi = useCallback((c: CariSatiri) => ac(c.id), [ac]);

  const ilkYukleme = q.isLoading;
  const yenileniyor = q.isFetching && !ilkYukleme;

  const aramaGirisi = (
    <input
      // Telefonda `search` DEĞİL `text`: iOS `search` girdisine 24 px'lik kendi temizleme düğmesini koyar.
      type={telefon ? 'text' : 'search'}
      inputMode="search"
      className={telefon ? `${FILTRE_GIRIS} w-full` : `${FILTRE_GIRIS} w-[220px] max-w-full`}
      placeholder="Cari kodu ya da adı"
      aria-label={telefon ? 'Cari kodu ya da adında ara' : undefined}
      value={ara}
      onChange={(e) => setAra(e.target.value)}
    />
  );
  const bilgi = `${SAYI.format(suzulen.length)} cari · ${tarihMetni(son)} tarihine kadar`;

  return (
    <div
      ref={doluRef}
      data-testid="cari-hesap-listesi"
      // Telefon: kök sayfa dolgusunun altını kapatır (`-mb-4`), toplam şeridi kenardan kenara ve dipte.
      className={telefon ? 'flex flex-col gap-2 -mb-4 rk-kenar-16' : 'flex flex-col gap-2 min-h-[320px]'}
    >
      <RaporFiltreBari
        testid="cari-filtre-bar"
        birincil={
          <FiltreAlani etiket="Bakiye" grup>
            <PeriodTabs value={tip} onChange={setTip} secenekler={BAKIYE_SECENEKLERI} kompakt />
          </FiltreAlani>
        }
        // Bugünden farklı bir tarih sonucu daraltır: kapalı panelde bu görünmeli.
        etkinAdlar={son !== bugun ? ['Şu tarihe kadar'] : []}
        ozet={ilkYukleme || !listeKaynagi ? undefined : yenileniyor ? 'Yenileniyor…' : bilgi}
      >
        {!telefon && <FiltreAlani etiket="Ara">{aramaGirisi}</FiltreAlani>}
        <FiltreAlani etiket="Şu tarihe kadar">
          <input
            type="date"
            className={FILTRE_GIRIS}
            value={sonGiris}
            onChange={(e) => {
              setSonGiris(e.target.value);
              if (gecerliTarih(e.target.value)) setSon(e.target.value);
            }}
            onBlur={() => { if (!gecerliTarih(sonGiris)) setSonGiris(son); }}
          />
        </FiltreAlani>
      </RaporFiltreBari>

      {/* Telefonda bu satır yok: aynı bilgi "Filtreler" başlığının özetinde (sayı ekranda tek yerde). */}
      {!telefon && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-h-[20px]">
          <div className="text-[13px] text-ink-500" data-testid="cari-bilgi">
            {ilkYukleme || !listeKaynagi ? (
              <>&nbsp;</>
            ) : (
              <>
                <b className="font-semibold text-ink-900">{SAYI.format(suzulen.length)}</b> cari · {tarihMetni(son)} tarihine kadar
                {yenileniyor && <span className="ml-2 text-ink-400" role="status">Yenileniyor…</span>}
              </>
            )}
          </div>
        </div>
      )}

      {!listeKaynagi ? (
        <RaporHatasi testid="cari-kaynaksiz" mesaj="Bu firmada cari hesap listesi kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="cari-hata" mesaj="Cari hesap listesi yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        <RaporTablosu
          testid="cari-tablosu"
          etiket="Cari Hesap Listesi"
          kolonlar={kolonlar}
          satirlar={suzulen}
          satirKimligi={(c) => c.id}
          onSatirTikla={satirTiklandi}
          seciliKimlik={seciliCari?.id ?? null}
          hamSatirSayisi={tum.length}
          bosMesaj={tum.length === 0 ? 'Bu tarihe kadar cari kaydı yok.' : 'Bu süzgeçle kayıt yok.'}
          yukleniyor={ilkYukleme}
          yenileniyor={yenileniyor}
          kartAraclari={aramaGirisi}
        />
      )}

      <CariHareketCekmecesi cari={seciliCari} kaynak={hareketKaynagi} belgeKaynagi={belgeKaynagi} sonTarih={son} onKapat={secim.kapat} />
    </div>
  );
}
